import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  Optional,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs';

import { IconName } from '../../icons-container/icons.type';
import { chartColorsSet } from '../../utils/colors-sets';
import { IconSize } from '../icon/icon.component';
import { AvatarThemeSubject } from './avatar-theme.directive';
import { AvatarSize, AvatarShape, AvatarType, AvatarBg, AvatarFontSize } from './avatar.types';

@Component({
  selector: 'rt-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'size + " " + shape',
  },
})
export class AvatarComponent implements OnChanges {
  @Input() public type: AvatarType;
  @Input() public shape: AvatarShape = 'oval';
  @Input() public size: AvatarSize = 'base';
  @Input() public backgroundColor: AvatarBg;
  @Input() public fontSize: AvatarFontSize;

  @Input() public imageSrc: string | null;
  @Input() public isOnline: boolean = false;
  @Input() public avatarTpl?: TemplateRef<any>;
  @Input() public fullName: string;
  @Input() public imageStyles: object;
  // isColorized property overrides backgroundColor
  @Input() public isColorized: boolean = false;
  @Input() public hasUnreadNotifications: boolean = false;
  @Input() public tooltipTplRef: TemplateRef<any>;
  @Input() public iconName: IconName;
  @Input() public isIconLight: boolean = false;

  @ViewChild('avatarPlaceholder') public avatarPlaceholder: ElementRef;

  public nameInitials: string;
  public iconSize: IconSize;

  private themeClassSub: Subscription;
  private currentThemeClass: string;

  constructor(
    private elRef: ElementRef,
    private renderer2: Renderer2,
    @Optional() private themeClass$: AvatarThemeSubject,
  ) {}

  public ngAfterViewInit(): void {
    if (this.themeClass$) {
      this.themeClassSub = this.themeClass$.subscribe(className =>
        this.toggleThemeClass(className!),
      );
    }

    this.setBackgroundColor();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.fullName && this.fullName) {
      const [firstName, ...lastName] = this.fullName.trim().split(' ');
      const char1 = firstName.trim() ? firstName[0].trim().toUpperCase() : '';
      const char2 = lastName.length ? lastName.join('').trim()[0].toUpperCase() : '';
      this.nameInitials = char1 + char2;
      this.setBackgroundColor();
    }

    if (changes.size && this.size) {
      this.iconSize = this.mapSizeToIconSize(this.size);
    }
  }

  public ngOnDestroy(): void {
    if (this.themeClassSub) {
      this.themeClassSub.unsubscribe();
    }
  }

  private toggleThemeClass(className: string): void {
    if (this.currentThemeClass) {
      this.renderer2.removeClass(this.elRef.nativeElement, this.currentThemeClass);
    }

    this.renderer2.addClass(this.elRef.nativeElement, className);
    this.currentThemeClass = className;
  }

  private setBackgroundColor(): void {
    if (
      !this.isColorized ||
      !this.nameInitials ||
      !this.avatarPlaceholder ||
      !this.avatarPlaceholder.nativeElement
    )
      return;

    const strCharNo = parseInt(
      [...this.nameInitials]
        .reduce((acc, l) => acc + l.charCodeAt(0) / 2, 0)
        .toString(10)
        .slice(1, 2),
      10,
    );

    this.renderer2.setStyle(
      this.avatarPlaceholder.nativeElement,
      'background-color',
      chartColorsSet[strCharNo].backgroundColor,
    );
  }

  private mapSizeToIconSize(size: AvatarSize) {
    switch (size) {
      case 'small-x':
        return 'small-xxx';
      case 'medium':
        return 'medium';
      case 'large':
        return 'large';
      case 'base':
      case 'small':
      default:
        return 'small-x';
    }
  }
}
